import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';

import type {
  ExportFieldPreset,
  ExportTemplateColumn,
  ExportTemplateDefinition,
} from '../../types/export.types';
import type { BrowseExportFilters } from '../../utils/export-browse-filters';
import { ExportColumnDrawer } from '../export-column-drawer';
import { ExportColumnManifestButton } from '../export-column-manifest-button';

import { BrowseFiltersPopover } from './browse-filters-popover';
import { BrowseTemplateRadios } from './browse-template-radios';

interface BrowseToolbarProps {
  // ── Narrowing the view ──
  searchInput: string;
  onSearchChange: (value: string) => void;
  exportFilters: BrowseExportFilters;
  onExportFiltersChange: (next: BrowseExportFilters) => void;
  /** Locales this run can be exported in; empty hides the Language filter. */
  languageOptions?: string[];
  /** Header-row filters currently applied, for the count and its Clear. */
  viewFilterCount: number;
  onClearViewFilters: () => void;
  // ── Shaping the file ──
  templates: ExportTemplateDefinition[];
  defaultTemplateCode: string | null;
  template: string | null;
  onTemplateChange: (code: string | null) => void;
  columns: ExportTemplateColumn[];
  selectedColumns: string[];
  onColumnsChange: (keys: string[]) => void;
  runFields: string[] | undefined;
  /** The connector's standard starting columns when `runFields` is unknown. */
  fallbackFields?: readonly string[] | undefined;
  unmatchedColumns: string[];
  presets: ExportFieldPreset[];
  transformations: Record<string, string>;
  // ── What the table holds ──
  total: number | undefined;
  isPending: boolean;
  isFetching: boolean;
}

/**
 * The controls above the table, in two groups that answer two different
 * questions.
 *
 * Left — *what am I looking at*: search and the export filters, plus the
 * header-row filter count when any are set. Right — *what will the file be*:
 * template, columns, and the live total. The split matters because the two
 * kinds of filter have different reach (see `BrowseExportBar`), and grouping
 * them by widget type hid that.
 */
export function BrowseToolbar({
  searchInput,
  onSearchChange,
  exportFilters,
  onExportFiltersChange,
  languageOptions = [],
  viewFilterCount,
  onClearViewFilters,
  templates,
  defaultTemplateCode,
  template,
  onTemplateChange,
  columns,
  selectedColumns,
  onColumnsChange,
  runFields,
  fallbackFields,
  unmatchedColumns,
  presets,
  transformations,
  total,
  isPending,
  isFetching,
}: BrowseToolbarProps) {
  const { t } = useTranslation('exports');

  return (
    <div className="bg-muted/30 flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <div className="relative w-[min(20rem,60vw)]">
          <Search
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
          />
          <Input
            value={searchInput}
            onChange={(event) => {
              onSearchChange(event.target.value);
            }}
            placeholder={t('browse.searchPlaceholder')}
            aria-label={t('browse.searchPlaceholder')}
            className="h-9 pl-8"
          />
          {searchInput !== '' && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2 cursor-pointer"
              aria-label={t('browse.clearSearch')}
              onClick={() => {
                onSearchChange('');
              }}
            >
              <X aria-hidden="true" className="size-3.5" />
            </Button>
          )}
        </div>

        <BrowseFiltersPopover
          filters={exportFilters}
          onApply={onExportFiltersChange}
          languageOptions={languageOptions}
        />

        {/* The count sits with the controls that produce it: this is how many
            products the search and filters leave, not a property of the file. */}
        <span className="flex items-center gap-2 pl-1">
          {isPending || total === undefined ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <span className="text-muted-foreground text-xs">
              {t('browse.total', { count: total })}
            </span>
          )}
          {/* Fetching but not first-load: the previous page stays on screen via
              `placeholderData`, so this is the only sign it is stale. */}
          {isFetching && !isPending && (
            <span className="text-muted-foreground text-xs">{t('browse.loading')}</span>
          )}
        </span>

        {viewFilterCount > 0 && (
          <span className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="cursor-help text-[11px] font-normal">
                  {t('browse.filterCount', { count: viewFilterCount })}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{t('browse.filterHint')}</TooltipContent>
            </Tooltip>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 cursor-pointer text-xs"
              onClick={onClearViewFilters}
            >
              {t('browse.clearFilters')}
            </Button>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <BrowseTemplateRadios
          templates={templates}
          defaultTemplateCode={defaultTemplateCode}
          template={template}
          onTemplateChange={onTemplateChange}
        />

        {columns.length > 0 && (
          <ExportColumnDrawer
            columns={columns}
            selected={selectedColumns}
            onApply={onColumnsChange}
            runFields={runFields}
            fallbackFields={fallbackFields}
            unmatched={unmatchedColumns}
            presets={presets}
            transformations={transformations}
            fileLabel="export"
            showManifestButton={false}
          />
        )}

        {/* Last on purpose: a download of the column list is the one thing here
            that changes nothing on screen. */}
        {columns.length > 0 && (
          <ExportColumnManifestButton
            columns={columns}
            selected={selectedColumns}
            transformations={transformations}
            fileLabel="export"
          />
        )}
      </div>
    </div>
  );
}
